import * as React from 'react';
import { useSelector } from 'react-redux';

import { updateBalloons } from './utils';
import { selectFreckles } from '../../../../../../common/selectors/freckles/selectFreckles';
import { IFrecklesFeaturesMap } from '../../../../../../common/types/freckles';
import { IFeaturesMap } from '../../../../../../common/types/map';
import { IApplicationState } from '../../../../../../common/types/redux';
import { geoJsonFeatureToRBushItem, geoJsonFrecklesFeatureToRBushItem } from '../../../../../../common/utils/features';
import { getFrecklesPointsTilesToUpdate, getTilesToUpdate } from '../../../../../../common/utils/tiles';
import { selectMapBounds } from '../../../../../selectors';
import { selectFeaturesForPrecision } from '../../../../../selectors/features/selectFeaturesForPrecision';
import { TMapBounds } from '../../../../../types/mapBounds';
import { useFeaturesContext } from '../context';

interface IFeaturesUpdaterProps {
  detailsVisible?: boolean;
}

export const FeaturesUpdater: React.FC<IFeaturesUpdaterProps> = ({ detailsVisible: isDetailsVisible }) => {
  const { rbush, rbushFreckles, pointsLayer, frecklesPointsLayer, hotspotsLayer, balloonManager } =
    useFeaturesContext();
  const { bounds, zoom } = useSelector<IApplicationState, TMapBounds>(selectMapBounds);
  const features = useSelector<IApplicationState, IFeaturesMap>(selectFeaturesForPrecision);
  const frecklesFeatures: IFrecklesFeaturesMap = useSelector(selectFreckles);

  // Основные точки и балуны
  React.useEffect(() => {
    const rbushItems = Object.values(features).map(f => geoJsonFeatureToRBushItem(f));
    updateBalloons(rbushItems, balloonManager);

    balloonManager.objectManager.removeAll();
    rbush.clear();

    if (rbushItems.length === 0) {
      pointsLayer.layer.update();

      return;
    }

    rbush.load(rbushItems);

    hotspotsLayer.layer.update();
    pointsLayer.layer.update();
    balloonManager.updateBalloons();

    const tilesToUpdate = getTilesToUpdate({ rbushItems, bounds, zoom, includeShadow: isDetailsVisible });

    if (tilesToUpdate.length > 0) {
      pointsLayer.layer.update(tilesToUpdate);
    }
  }, [features]);

  // Freckles (веснушки) - не проверенные посуточные объекты на карте в виде маленьких точек
  React.useEffect(() => {
    rbushFreckles.clear();

    const rbushFrecklesItems = Object.values(frecklesFeatures).map(geoJsonFrecklesFeatureToRBushItem);

    if (rbushFrecklesItems.length === 0 || zoom >= 13) {
      frecklesPointsLayer.layer.update();

      return;
    }

    rbushFreckles.load(rbushFrecklesItems);

    /*
        Иногда bounds принимает undefined, когда в стейте вызывается bboxToMapBounds.
        Но, когда в сторе изменились bounds, на это реагирует MapBoundUpdater и диспатчит уже другие bounds в стор
        src/shared/Map/containers/Map/MapBoundsUpdater/MapBoundsUpdater.ts:52
        Но в ходе этих манипуляций крашится приложение, если bounds = undefined (пока MapBoundUpdater не задиспатчил bounds).
        Проверка ниже нужна, чтобы этого избежать, она не влияет на работу приложения
     */
    if (bounds) {
      const tilesToUpdate = getFrecklesPointsTilesToUpdate({ rbushItems: rbushFrecklesItems, bounds, zoom });

      if (tilesToUpdate.length > 0) {
        frecklesPointsLayer.layer.update(tilesToUpdate);
      }
    }
  }, [frecklesFeatures, zoom, bounds]);

  return null;
};
